// Gün Sonu Raporu sayfası kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'sales.endOfDay';

export const endOfDayGuide = (t: TFunction): GuideContent => ({
  layout: 'report',
  title: t(`${G}.title`, 'Gün Sonu Raporu nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Seçtiğiniz zaman aralığındaki tüm satış kanallarının (hızlı satış, paket, restoran, kiosk, QR menü, yemek platformları) ödemelerini toplar ve gün sonunu kaydeder. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    actions: t(`${G}.regions.actions`, 'Gün Sonu Yap · Fiş · Excel'),
    filters: t(`${G}.regions.filters`, 'Tarih, şube, ekran'),
    totals: t(`${G}.regions.totals`, 'Ödeme özeti'),
    table: t(`${G}.regions.table`, 'Kanal sekmeleri ve satışlar'),
  },
  steps: [
    {
      region: 'filters',
      title: t(`${G}.s1.title`, '1. Şube ve zaman aralığını seçin'),
      text: t(`${G}.s1.text`, 'Başlangıç bugün 00:00, Bitiş şu an olarak gelir; saatleriyle birlikte değiştirebilirsiniz. Şube listesinden bir veya birkaç şube seçin; boş bırakırsanız Tüm Şubeler alınır. "Hızlı Satış / PYK Ekranı" ile belirli ekranları seçebilirsiniz. Sonra "Filtrele"ye basın.'),
      tips: [
        t(`${G}.s1.tip1`, 'Şube yöneticisi olarak girdiyseniz şube seçimi kendi şubenize kilitlidir.'),
      ],
    },
    {
      region: 'filters',
      title: t(`${G}.s2.title`, '2. Son gün sonundan sonrasını alın'),
      text: t(`${G}.s2.text`, '"Son Gün Sonu:" kutusu en son kaydedilen gün sonunun bitiş zamanını gösterir. "Aktar"a basınca Başlangıç o zamandan 1 saniye sonrasına, Bitiş şu ana ayarlanır; böylece hiçbir satış iki kez sayılmaz ya da arada kalmaz. Ardından "Filtrele"ye basın.'),
      tips: [
        t(`${G}.s2.tip1`, '"Son Gün Sonu" şirketinizin tüm şubelerindeki en son gün sonu kaydına bakar; şube şube gün sonu yapıyorsanız tarihleri kontrol edin.'),
      ],
    },
    {
      region: 'totals',
      title: t(`${G}.s3.title`, '3. Kanal toplamlarını okuyun'),
      text: t(`${G}.s3.text`, 'Özet tablosu bütün kanallardaki ödemeleri Ödeme Türü ve Kasa / Banka bazında toplar. Nakit ödemeler ödemenin girdiği kasa adıyla, POS ödemeleri banka hesabı adıyla, platform ödemeleri "Getir - Nakit", "Y.Sepeti - POS" gibi satırlarla görünür. En alttaki GENEL TOPLAM bu satırların toplamıdır.'),
      tips: [
        t(`${G}.s3.tip1`, 'Açık Hesap ve online ödemeler (ör. "… (Online)") ayrı satırdadır ve GENEL TOPLAM\'a dahildir.'),
        t(`${G}.s3.tip2`, 'Ödemesi alınmamış paket / platform siparişleri "… - Ödenmemiş (Bilinmiyor)" satırında görünür ve GENEL TOPLAM\'a dahildir.'),
        t(`${G}.s3.tip3`, 'Ön Ödemeli (PYK kartıyla) satışlar ayrı satırdadır ve GENEL TOPLAM\'a dahildir; karta bakiye yükleme bu rapora girmez.'),
        t(`${G}.s3.tip4`, 'Restoran İptal, Hızlı Satış İptal ve Diğer İptal satırları bilgi içindir; GENEL TOPLAM\'a eklenmez.'),
        t(`${G}.s3.tip5`, 'Yemek Platformları İndirim Toplamı ve Hızlı Satış İndirim Toplamı da bilgi içindir; toplamlar zaten indirimden sonraki tutardır.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s4.title`, '4. Satış detayına bakın'),
      text: t(`${G}.s4.text`, 'Sekmeler kanalları ayırır: Hızlı Satış, Paket Sipariş, Restoran, Kiosk, QR Menü, Getir, Migros, Yemeksepeti, Uber Eats; yanlarında kayıt sayısı yazar. Hızlı Satış, Paket Sipariş, Restoran ve platform sekmelerinde bir satıra tıklayınca o satışın veya siparişin detayı açılır (ürünler, ödemeler, fatura).'),
      tips: [
        t(`${G}.s4.tip1`, 'Açık Masalar ve Oturumu Açık Kartlar tabloları "Gün Sonuna Dahil Edilmeyecek" olarak gösterilir.'),
      ],
    },
    {
      region: 'actions',
      title: t(`${G}.s5.title`, '5. Gün sonunu onaylayın'),
      text: t(`${G}.s5.text`, '"Gün Sonu Yap" düğmesi Filtrele\'den sonra ve aralıkta kayıt varsa açılır. Açılan onay penceresinde şubeyi, kaydedilecek tarih aralığını ve Genel Toplam, Nakit, Açık Hesap, İptal, POS İade tutarlarını kontrol edip onaylayın; "Gün Sonu Başarılı!" penceresinde rapor numarası görünür.'),
      tips: [
        t(`${G}.s5.tip1`, 'Gün sonu yalnız bir rapor kaydıdır: o anki toplamları saklar. Satışları, kasa / banka bakiyelerini veya stoğu değiştirmez.'),
        t(`${G}.s5.tip2`, 'Kaydedilen gün sonu bu ekrandan veya Gün Sonları listesinden silinemez, geri alınamaz. Onaylamadan önce tarihleri kontrol edin.'),
        t(`${G}.s5.tip3`, 'Seçilen şube için tarih aralığı daha önceki bir gün sonuyla çakışıyorsa kayıt yapılmaz; "Aktar" ile son gün sonundan sonrasını seçin.'),
        t(`${G}.s5.tip4`, 'Açık masa varsa uyarı çıkar: bu masaların satışları bir sonraki gün sonuna kalır.'),
        t(`${G}.s5.tip5`, 'Birden fazla şube seçiliyse ya da hiç şube seçilmemişse gün sonu tek bir genel (tüm şubeler) kaydı olarak saklanır.'),
      ],
    },
    {
      region: 'actions',
      title: t(`${G}.s6.title`, '6. 80mm fiş veya Excel alın'),
      text: t(`${G}.s6.text`, 'Yazıcı simgesi (Fiş Yazdır (80mm)) "KASA RAPORU" fişini basar: ürün satış özeti, ödeme detayları, iptaller ve ödemesi alınmayan siparişler. Excel İndir raporu dosyaya aktarır. İkisi de Filtrele\'den sonra açılır. Başlıktaki (i) bu rehberi açar.'),
      tips: [
        t(`${G}.s6.tip1`, 'Yazdırma penceresi açılmazsa tarayıcının açılır pencere engelleyicisini kontrol edin.'),
      ],
    },
  ],
});
